import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { Button } from "@devhop/ui";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, PencilIcon } from "lucide-react";
import { useProductQuery } from "../api/queries";
import {
  getCatalogModeForKind,
  getProductCatalogConfig,
  isCatalogModeMatch,
  type ProductCatalogMode,
} from "../ui/product-catalog-config";
import { ProductDetailInfo } from "../ui/ProductDetailInfo";
import { ProductImagesSection } from "../ui/ProductImagesSection";
import { ProductVariantsSection } from "../ui/ProductVariantsSection";

function nameLo(name: unknown): string {
  if (typeof name === "object" && name !== null) {
    const o = name as { lo?: string; en?: string; th?: string };
    return o.lo || o.en || o.th || "—";
  }
  return String(name);
}

type ProductDetailPageProps = {
  catalogMode?: ProductCatalogMode;
};

export function ProductDetailPage({
  catalogMode = "retail",
}: ProductDetailPageProps) {
  const config = getProductCatalogConfig(catalogMode);
  const { id } = useParams({ strict: false });
  const nav = useNavigate();
  const { data: product, isLoading } = useProductQuery(id ?? "");

  if (isLoading) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: config.labels.breadcrumb, to: config.routes.list },
              { label: id ?? "ລາຍລະອຽດ" },
            ]}
          />
        </Header>
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
          </div>
        </Main>
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: config.labels.breadcrumb, to: config.routes.list },
              { label: id ?? "ບໍ່ພົບ" },
            ]}
          />
        </Header>
        <Main>
          <div className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">ບໍ່ພົບສິນຄ້າ</p>
          </div>
        </Main>
      </>
    );
  }

  const isWrongCatalog = !isCatalogModeMatch(catalogMode, product.kind);

  if (isWrongCatalog) {
    const otherConfig = getProductCatalogConfig(
      getCatalogModeForKind(product.kind),
    );
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
              { label: config.labels.breadcrumb, to: config.routes.list },
              { label: nameLo(product.name) },
            ]}
          />
        </Header>
        <Main>
          <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
            <p className="text-muted-foreground">
              ລາຍການນີ້ຢູ່ໃນເມນູ {otherConfig.labels.breadcrumb}
            </p>
            <Button asChild variant="outline">
              <Link
                to={otherConfig.routes.detail}
                params={{ id: product.id }}
              >
                ໄປທີ່ {otherConfig.labels.breadcrumb}
              </Link>
            </Button>
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: config.labels.breadcrumb, to: config.routes.list },
            { label: nameLo(product.name) },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-bold text-2xl">{nameLo(product.name)}</h1>
            <p className="text-muted-foreground text-sm">
              {config.labels.detailDescription}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => nav({ to: config.routes.list })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
            <Button
              onClick={() =>
                nav({
                  to: config.routes.edit,
                  params: { id },
                })
              }
            >
              <PencilIcon className="size-4" />
              ແກ້ໄຂ
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          <ProductDetailInfo product={product} />

          {config.showVariantsColumn && (
            <ProductVariantsSection productId={id ?? ""} product={product} />
          )}

          <ProductImagesSection productId={id ?? ""} />
        </div>
      </Main>
    </>
  );
}
